import { useIsMobileActive } from '../../../hooks/useIsMobileActive';
import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useApiBase } from '../../../hooks/useApiBase';
import { createContact, checkContactDuplicates, type ContactDuplicateMatch } from '../../../api/contacts';
import { formatPhoneNumber } from '../../../lib/phone';

export interface QuickCreateContactDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Callback when contact is created or existing contact is selected */
  onContactCreated?: (contactId: string) => void;
  /** Default tag to assign to new contact (defaults to 'Customer') */
  defaultTag?: string;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const initialFormData: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
};

/**
 * Lightweight dialog for quickly creating a contact with just essential fields.
 * Includes duplicate detection with warnings for exact email/phone matches
 * and soft warnings for similar names.
 */
export function QuickCreateContactDialog({
  open,
  onOpenChange,
  onContactCreated,
  defaultTag = 'Customer',
}: QuickCreateContactDialogProps) {
  const mobile = useIsMobileActive();
  const apiBase = useApiBase();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<ContactDuplicateMatch[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setDuplicateMatches([]);
    setErrors({});
    setSaving(false);
  }, []);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  }, [onOpenChange, resetForm]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear duplicate matches when email or phone changes
    if (field === 'email' || field === 'phone') {
      setDuplicateMatches([]);
    }
  }, [errors]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Name validation: either first or last name required
    if (!formData.first_name.trim() && !formData.last_name.trim()) {
      newErrors.first_name = 'Either first name or last name is required';
    }

    // Contact info validation: either email or phone required
    if (!formData.email.trim() && !formData.phone.trim()) {
      newErrors.email = 'Either email or phone is required';
    }

    // Basic email format validation if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check for hard warnings blocking submission
    const currentHardWarnings = duplicateMatches.filter(
      (match) => match.match_type === 'exact_email' || match.match_type === 'exact_phone'
    );
    if (currentHardWarnings.length > 0) {
      return;
    }

    setSaving(true);
    setDuplicateMatches([]);

    try {
      // Check for duplicates
      const duplicates = await checkContactDuplicates(apiBase, {
        first_name: formData.first_name.trim() || undefined,
        last_name: formData.last_name.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      });

      const hardWarnings = duplicates.filter(
        (match) => match.match_type === 'exact_email' || match.match_type === 'exact_phone'
      );
      const softWarnings = duplicates.filter((match) => match.match_type === 'similar_name');

      // Hard warnings block creation
      if (hardWarnings.length > 0) {
        setDuplicateMatches(hardWarnings);
        setSaving(false);
        return;
      }

      // Soft warnings are shown but allow creation
      if (softWarnings.length > 0) {
        setDuplicateMatches(softWarnings);
      }

      // Create the contact
      const newContact = await createContact(apiBase, {
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        tags: [defaultTag],
      });

      onContactCreated?.(newContact.id);
      handleOpenChange(false);
    } catch (err: any) {
      console.error('Failed to create contact:', err);
      setErrors({ first_name: err?.message || 'Failed to create contact' });
    } finally {
      setSaving(false);
    }
  }, [apiBase, defaultTag, duplicateMatches, formData, handleOpenChange, onContactCreated, validateForm]);

  const handleSelectExistingContact = useCallback((contactId: string) => {
    onContactCreated?.(contactId);
    handleOpenChange(false);
  }, [handleOpenChange, onContactCreated]);

  const hasHardWarnings = duplicateMatches.some(
    (match) => match.match_type === 'exact_email' || match.match_type === 'exact_phone'
  );
  const hasSoftWarnings = duplicateMatches.some((match) => match.match_type === 'similar_name');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Quick Create Contact</DialogTitle>
          <DialogDescription>
            Add a new contact with essential information.
          </DialogDescription>
        </DialogHeader>

        {/* Hard warnings (block creation) */}
        {hasHardWarnings && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Duplicate Contact Found</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>A contact with the same email or phone already exists:</p>
              {duplicateMatches
                .filter((match) => match.match_type === 'exact_email' || match.match_type === 'exact_phone')
                .map((match, index) => (
                  <div key={index} className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                    <div className="font-medium">{match.contact.name}</div>
                    {match.contact.email && <div className="text-sm">Email: {match.contact.email}</div>}
                    {match.contact.phone && (
                      <div className="text-sm">
                        Phone: {formatPhoneNumber(match.contact.phone) || match.contact.phone}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => handleSelectExistingContact(match.contact.id)}
                    >
                      Use This Contact
                    </Button>
                  </div>
                ))}
              <p className="text-sm font-medium">Please use the existing contact or modify email/phone.</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Soft warnings (allow creation) */}
        {hasSoftWarnings && !hasHardWarnings && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Similar Contact Found</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>A contact with a similar name exists:</p>
              {duplicateMatches
                .filter((match) => match.match_type === 'similar_name')
                .map((match, index) => (
                  <div key={index} className="rounded-md border border-warning/40 bg-warning/10 p-3">
                    <div className="font-medium">{match.contact.name}</div>
                    {match.contact.email && <div className="text-sm">Email: {match.contact.email}</div>}
                    {match.contact.phone && (
                      <div className="text-sm">
                        Phone: {formatPhoneNumber(match.contact.phone) || match.contact.phone}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => handleSelectExistingContact(match.contact.id)}
                    >
                      Use This Contact Instead
                    </Button>
                  </div>
                ))}
              <p className="text-sm">You can proceed or use the existing contact.</p>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="John"
                disabled={saving}
                autoFocus={!mobile}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Doe"
                disabled={saving}
              />
            </div>
          </div>
          {errors.first_name && (
            <p className="text-sm text-destructive">{errors.first_name}</p>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="john@example.com"
              disabled={saving}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="(808) 555-1234"
              disabled={saving}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || hasHardWarnings}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
